import { Injectable, Injector } from '@angular/core';
import {
	ActivatedRoute,
	ActivatedRouteSnapshot,
	NavigationEnd,
	Router
} from '@angular/router';
import { BehaviorSubject, concat, Observable, of } from 'rxjs';
import { distinct, filter, first, mergeMap, toArray } from 'rxjs/operators';
import { IBreadcrumb, wrapIntoObservable } from '../breadcrumb.shared';
import { BreadcrumbConfig } from './breadcrumb.config';
import { BreadcrumbResolver } from './breadcrumb.resolver';

@Injectable()
export class BreadcrumbService {
	private _breadcrumbs = new BehaviorSubject<IBreadcrumb[]>([]);
	private _defaultResolver = new BreadcrumbResolver();

	constructor(
		private _router: Router,
		route: ActivatedRoute,
		private _config: BreadcrumbConfig,
		private _injector: Injector
	) {
		this._router.events
			.pipe(filter((x) => x instanceof NavigationEnd))
			.subscribe(() => {
				const currentRoot = _router.routerState.snapshot.root;

				// Observable.of(this._config.prefixCrumbs)
				this._resolveCrumbs(currentRoot)
					.pipe(
						mergeMap((x) => x),
						distinct((x) => x.text),
						toArray(),
						mergeMap((x) => {
							if (this._config.postProcess) {
								const y$ = this._config.postProcess(x);
								return wrapIntoObservable<IBreadcrumb[]>(
									y$
								).pipe(first());
							} else {
								return of(x);
							}
						})
					)
					.subscribe((x) => {
						this._breadcrumbs.next(x);
					});
			});
	}

	get crumbs$(): Observable<IBreadcrumb[]> {
		return this._breadcrumbs;
	}

	private _resolveCrumbs(
		route: ActivatedRouteSnapshot
	): Observable<IBreadcrumb[]> {
		let crumbs$: Observable<IBreadcrumb[]>;

		const data = route.routeConfig && route.routeConfig.data;

		if (data && data.breadcrumbs) {
			let resolver: BreadcrumbResolver;

			if (data.breadcrumbs.prototype instanceof BreadcrumbResolver) {
				resolver = this._injector.get<BreadcrumbResolver>(
					data.breadcrumbs
				);
			} else {
				resolver = this._defaultResolver;
			}

			const result$ = resolver.resolve(route);
			crumbs$ = wrapIntoObservable<IBreadcrumb[]>(result$).pipe(first());
		} else {
			crumbs$ = of([]);
		}

		if (route.firstChild) {
			crumbs$ = concat(crumbs$, this._resolveCrumbs(route.firstChild));
			// crumbs$ = crumbs$.pipe(concat(this._resolveCrumbs(route.firstChild)));
		}

		return crumbs$;
	}
}
